import { dataLayer, pushEvent as pushDataLayerEvent, pushOnce } from "utils/data-layer"

type Offer = "monthly" | "yearly"

interface Price {
  unit_amount: number
  currency: string
  price_id: string
}

const SUPPORTED_UTM_KEYS = [
  "utm_medium",
  "utm_source",
  "utm_campaign",
  "utm_id",
  "utm_content",
  "utm_term",
] as const

function utmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search) // eslint-disable-line compat/compat

  return SUPPORTED_UTM_KEYS.reduce<Record<string, string>>((memo, key) => {
    const value = params.get(key)
    if (!value) return memo

    return {
      ...memo,
      [key]: value,
    }
  }, {})
}

function amountFromMinorUnits(amount: number): number {
  return amount / 100
}

function buildItems(offer: Offer, price: Price): Array<Record<string, unknown>> {
  return [
    {
      item_id: price.price_id,
      item_name: "Mountain-Forecast Membership",
      item_category: "subscription",
      item_variant: offer,
      price: amountFromMinorUnits(price.unit_amount),
      quantity: 1,
    },
  ]
}

function pushEvent(event: string, params: Record<string, unknown> = {}): void {
  pushDataLayerEvent(event, { ...utmParams(), ...params })
}

function pushEcommerceEvent(event: string, ecommerce: Record<string, unknown>): void {
  dataLayer().push({ ecommerce: null })
  pushEvent(event, { ecommerce })
}

function ecommerceData(offer: Offer, price: Price): Record<string, unknown> {
  return {
    currency: price.currency.toUpperCase(),
    value: amountFromMinorUnits(price.unit_amount),
    items: buildItems(offer, price),
  }
}

function trackSafely(callback: () => void): void {
  try {
    callback()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Could not track checkout event", error)
  }
}

export function trackBeginCheckout(offer: Offer, price: Price): void {
  trackSafely(() => {
    pushOnce(`begin_checkout:${price.price_id}:${price.unit_amount}`, () => {
      pushEcommerceEvent("begin_checkout", ecommerceData(offer, price))
    })
  })
}

export function trackAddPaymentInfo(offer: Offer, price: Price, paymentType: string): void {
  trackSafely(() => {
    pushEcommerceEvent("add_payment_info", {
      ...ecommerceData(offer, price),
      payment_type: paymentType,
    })
  })
}

export function trackPurchase(transactionId: string | null, offer: Offer, price: Price): void {
  if (!transactionId) return

  trackSafely(() => {
    pushOnce(`purchase:${transactionId}`, () => {
      pushEcommerceEvent("purchase", {
        ...ecommerceData(offer, price),
        transaction_id: transactionId,
      })
    })
  })
}
